import { useEffect, useMemo, useState } from "react";

const API_ROOT = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");

const formatDate = (value) => {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-GB");
};

const getDayName = (value) => {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-US", { weekday: "long" });
};

const monthOptions = [
  { value: "", label: "All Months" },
  { value: "1", label: "January" },
  { value: "2", label: "February" },
  { value: "3", label: "March" },
  { value: "4", label: "April" },
  { value: "5", label: "May" },
  { value: "6", label: "June" },
  { value: "7", label: "July" },
  { value: "8", label: "August" },
  { value: "9", label: "September" },
  { value: "10", label: "October" },
  { value: "11", label: "November" },
  { value: "12", label: "December" }
];

export default function HolidayList() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const currentYear = new Date().getFullYear();
  const years = useMemo(() => {
    const out = [];
    for (let y = 2024; y <= currentYear + 2; y += 1) out.push(y);
    return out;
  }, [currentYear]);

  const [filters, setFilters] = useState({
    searchName: "",
    year: String(currentYear),
    month: ""
  });

  const fetchHolidays = async () => {
    try {
      setLoading(true);
      setError("");
      const params = new URLSearchParams(filters);
      const res = await fetch(`${API_ROOT}/api/holidays/list?${params.toString()}`);
      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.message || "Failed to fetch holiday list");
      }

      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      setRows([]);
      setError(err.message || "Failed to fetch holiday list");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHolidays();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <section className="container-fluid">
      <h3 className="mb-3">Holiday List</h3>

      {error && <div className="alert alert-danger">{error}</div>}

      <div className="row g-3 align-items-end mb-3">
        <div className="col-lg-4 col-md-6">
          <label className="form-label">Search by Holiday Name</label>
          <input
            className="form-control"
            value={filters.searchName}
            onChange={(e) => setFilters((prev) => ({ ...prev, searchName: e.target.value }))}
            placeholder="Type holiday name"
          />
        </div>

        <div className="col-lg-2 col-md-6">
          <label className="form-label">Year</label>
          <select
            className="form-control"
            value={filters.year}
            onChange={(e) => setFilters((prev) => ({ ...prev, year: e.target.value }))}
          >
            <option value="">All Years</option>
            {years.map((y) => (
              <option key={y} value={String(y)}>{y}</option>
            ))}
          </select>
        </div>

        <div className="col-lg-2 col-md-6">
          <label className="form-label">Month</label>
          <select
            className="form-control"
            value={filters.month}
            onChange={(e) => setFilters((prev) => ({ ...prev, month: e.target.value }))}
          >
            {monthOptions.map((m) => (
              <option key={m.value || "all"} value={m.value}>{m.label}</option>
            ))}
          </select>
        </div>

        <div className="col-lg-2 col-md-6">
          <button className="btn btn-primary w-100" type="button" onClick={fetchHolidays} disabled={loading}>
            {loading ? "Loading..." : "View"}
          </button>
        </div>
      </div>

      <div className="table-responsive">
        <table className="table table-bordered table-striped">
          <thead style={{ '--bs-table-bg': '#008080', color: 'white' }}>
            <tr>
              <th>SNo</th>
              <th>Holiday Date</th>
              <th>Day</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            {!rows.length && (
              <tr>
                <td colSpan={4} className="text-center text-muted">
                  {loading ? "Loading..." : "No records found. Apply filters and click View."}
                </td>
              </tr>
            )}
            {rows.map((row, idx) => (
              <tr key={`${row.Holidate}-${idx}`}>
                <td>{idx + 1}</td>
                <td>{formatDate(row.Holidate)}</td>
                <td>{getDayName(row.Holidate)}</td>
                <td>{row.Description}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
